import React, { type ReactNode } from 'react';
import { envType } from '@atlassiansox/analytics-web-client';
import {
	Coordination,
	CoordinationClient as EngagekitCoordinationClient,
} from '@atlassiansox/engagekit-ts';
import { MessageDeliveryStatus, ProductIds } from '@atlassian/ipm-choreographer';
import {
	ChoreographerContextProvider,
	useChoreographedStatusContext,
} from '@atlassian/jira-choreographer-services/src/ChoreographerContextProvider.tsx';
import type { MaybeChoreographedComponentProps } from '@atlassian/jira-choreographer-services/src/types.tsx';
import { shouldBeChoreographed } from '@atlassian/jira-choreographer-services/src/utils.tsx';

import { getTenantContext_DEPRECATED_DO_NOT_USE } from '@atlassian/jira-common-util-get-tenant-context/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import getMeta from '@atlassian/jira-get-meta';
import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id/src/index.tsx';
import {
	toEnvironment,
	type Environment,
} from '@atlassian/jira-shared-types/src/tenant-context.tsx';

export type Props = {
	shouldAllowOnSynthetic?: boolean;
	messageId: string;
	children?: ReactNode;
	fallback?: ReactNode;
};

const isSyntheticTenant = () => getMeta('ajs-is-synthetic') === 'true';

// This is a modified CoordinationClient that does not allow external messages to show on
// synthetic tenants, due to the unexpected interruptions they can cause on tests (by overlapping page elements ).
// If required, you can opt in to show your external message on synthetic tenants using the `shouldAllowOnSynthetic` argument.
export class CoordinationClient extends EngagekitCoordinationClient {
	shouldUseMockClient: boolean;

	isDebugModeEnabled: boolean;

	enableChoreographer: boolean;

	onMessageDisposition: MaybeChoreographedComponentProps['onMessageDisposition'];

	constructor(
		cloudId: string,
		stargateUrl: string,
		shouldAllowOnSynthetic = false,
		orgId = '',
		messageType: MaybeChoreographedComponentProps['messageType'] = 'transactional',
		onMessageDisposition: MaybeChoreographedComponentProps['onMessageDisposition'] = undefined,
		isAlreadyChoreographedAtAncestor = false,
	) {
		const environment = toEnvironment(getMeta('ajs-environment'));
		const getEnvironmentType = (env: Environment | null) => {
			if (env === 'prod') {
				return envType.PROD;
			}
			if (env === 'staging') {
				return envType.STAGING;
			}
			return envType.DEV;
		};

		// If a ancestor (e.g. CoordinationClient wraps CoordinationClient) is already choreographed,
		// we should not re-choreograph this CoordinationClient
		const enableChoreographer =
			shouldBeChoreographed({ messageType }) && !isAlreadyChoreographedAtAncestor;

		const coordinationOptions = {
			enableChoreographer,
			envType: getEnvironmentType(environment),
			additionalData: { sourceProductId: ProductIds.JIRA },
		};

		super({ cloudId, stargateUrl, orgId, isFedRAMPEnvironment: false, coordinationOptions });
		this.shouldUseMockClient = isSyntheticTenant() && !shouldAllowOnSynthetic;
		this.enableChoreographer = enableChoreographer;
		this.onMessageDisposition = onMessageDisposition;
		// // This is a permanant flag: go/jira-ep-debugging
		this.isDebugModeEnabled = fg('ep-external-message-debug-mode');
	}

	async start(messageId: string): Promise<boolean> {
		if (this.isDebugModeEnabled) {
			const serverResponse = await super.start(messageId);
			if (this.enableChoreographer) {
				this.onMessageDisposition?.(
					serverResponse ? MessageDeliveryStatus.STARTED : MessageDeliveryStatus.BLOCKED,
				);
			}
			// eslint-disable-next-line no-alert, no-restricted-globals
			const shouldShowMessage = confirm(
				`🐞 𝗘𝗣 𝗘𝘅𝘁𝗲𝗿𝗻𝗮𝗹 𝗠𝗲𝘀𝘀𝗮𝗴𝗲 𝗗𝗲𝗯𝘂𝗴𝗴𝗲𝗿

Message ID: ${messageId}
Start call server response: ${serverResponse}

Do you want to display the message?`,
			);
			if (!shouldShowMessage) {
				const didStop = await super.stop(messageId);
				if (this.enableChoreographer) {
					this.onMessageDisposition?.(
						didStop ? MessageDeliveryStatus.STOPPED : MessageDeliveryStatus.BLOCKED,
					);
				}
			}
			return shouldShowMessage;
		}
		return this.shouldUseMockClient
			? false // never allow messages to start
			: super.start(messageId).then((didStart) => {
					if (this.enableChoreographer) {
						this.onMessageDisposition?.(
							didStart ? MessageDeliveryStatus.STARTED : MessageDeliveryStatus.BLOCKED,
						);
					}
					return didStart;
				});
	}

	async stop(messageId: string): Promise<boolean> {
		return this.shouldUseMockClient
			? true // always allow messages to stop
			: super.stop(messageId).then((didStop) => {
					if (this.enableChoreographer) {
						this.onMessageDisposition?.(
							didStop ? MessageDeliveryStatus.STOPPED : MessageDeliveryStatus.BLOCKED,
						);
					}
					return didStop;
				});
	}
}

/**
 * This Component passes a "stop" prop to it's children which you can call as a function in order to
 * declare that you are done with showing your message.
 * Please refer to playbook/coordination-service.md for more details.
 */
export default function CoordinationClientComponent(
	props: Props & Partial<MaybeChoreographedComponentProps>,
) {
	const { cloudId } = getTenantContext_DEPRECATED_DO_NOT_USE();
	const stargateUrl = '/gateway/api';
	const { data: orgId } = useOrgId();

	const { isAlreadyChoreographedAtAncestor } = useChoreographedStatusContext();

	const client = new CoordinationClient(
		cloudId,
		stargateUrl,
		props.shouldAllowOnSynthetic,
		orgId,
		props.messageType,
		props.onMessageDisposition,
		isAlreadyChoreographedAtAncestor,
	);

	const { fallback, messageId } = props;

	return (
		<ChoreographerContextProvider isChoreographed={client.enableChoreographer}>
			<Coordination
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				client={client as CoordinationClient}
				fallback={fallback}
				messageId={messageId}
			>
				{/* eslint-disable-next-line @typescript-eslint/consistent-type-assertions */}
				{props.children as JSX.Element}
			</Coordination>
		</ChoreographerContextProvider>
	);
}
